import airbrake from 'airbrake'
import { flushDrafts } from 'logic/dossierItems/draftPersist'

// Classifies an API error response so the UI can react appropriately instead of treating
// every failure as a session timeout.
//
// The backend (app/controllers/api/api_controller.rb) returns 403 for two unrelated cases:
// a genuine session/CSRF problem (InvalidAuthenticityToken, which sets detail:
// 'session expired') and a plain authorization failure (Pundit::NotAuthorizedError, code
// 'forbidden', no detail). Only the former should trigger the "Sessie verlopen" modal.
//
// `errorObj` is the optional parsed JSON-API error (e.g. json.errors[0]); it may be absent
// when only an HTTP status is available (e.g. the heartbeat code path).
export function classifyApiError(status, errorObj = {}) {
  const detail = errorObj && errorObj.detail

  if (status === 401) return 'session'
  if (status === 403) return detail === 'session expired' ? 'session' : 'forbidden'
  if (status === 404) return 'notFound'
  if (status === 422) return 'validation'
  if (status >= 500) return 'server'

  return 'unknown'
}

// The "mainModal" is shared between the real "session expired" prompt and a distinct
// "no access" prompt; `kind` tells MainModal which copy/action to render.
export function showMainModal(kind) {
  return function(dispatch) {
    dispatch({ type: 'UPDATE_UI_STATE', payload: { key: 'mainModal', name: 'kind', value: kind } })
    dispatch({ type: 'UPDATE_UI_STATE', payload: { key: 'mainModal', name: 'isMainModalVisible', value: true } })
  }
}

export function expireSession() {
  return async function(dispatch) {
    await flushDrafts()
    dispatch({ type: 'CLEAR_STORE' })
    dispatch(showMainModal('session'))
  }
}

// Central place that decides how the UI reacts to an API error, so a permission error or
// a transient server error is no longer mislabeled as a session timeout.
// `err` is the optional JSON-API error object (json.errors[0]); returns the resolved kind.
export function handleApiError(status, err = {}) {
  return function(dispatch) {
    const kind = classifyApiError(status, err)

    switch (kind) {
      case 'session':
        dispatch(expireSession())
        break
      case 'forbidden':
        dispatch(showMainModal('forbidden'))
        break
      case 'notFound':
        dispatch(showMainModal('notFound'))
        break
      default: // 'server' / 'validation' / 'unknown'
        if (kind === 'unknown') {
          airbrake.notify({ error: new Error('Unexpected API response: ' + status) })
        }
        dispatch(showMainModal('error'))
    }

    return kind
  }
}

// Reads a non-success fetch Response's JSON-API error body and routes it through
// handleApiError. Shared by the fetch and upsert paths so they handle errors identically.
// Resolves to null so the caller can early-return from the following .then().
export function handleErrorResponse(response) {
  return function(dispatch) {
    return response.json().catch(() => ({})).then(body => {
      const err = (body.errors && body.errors[0]) || {}
      dispatch(handleApiError(response.status, err))
      return null
    })
  }
}
